import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Text, Button, SelectInput, TextInput, DateInput, Icon, Switch, Status} from '@/ui-kits';
import {DataTable, AddHospitalPocModal} from '@/components';
import {NA, statusOptions} from '@/constants';
import {SelectInputitem, GetHospitalPocListRequestPayload, DataTableColumn, HospitalPoc, AddHospitalPocRequestPayload} from '@/interface';
import {useDispatch, useSelector} from 'react-redux';
import {getHospitalPocListRequest, resetHospitalAdminMessage, updateHospitalPocStatusRequest, addHospitalPocRequest} from '@/services/redux/slices';
import {
  hospitalAdminPocListSelector,
  hospitalAdminPocListCurrentPageSelector,
  hospitalAdminPocListTotalPagesSelector,
  hospitalAdminPocListTotalResultsSelector,
  hospitalAdminLoadingSelector,
  hospitalAdminSuccessSelector,
  hospitalAdminFailureSelector,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage, SuccessCodes, getSuccessMessage} from '@/utils/getMessages';
import {useToast, useConfimationModal, useDebounceState} from '@/hooks';
import {formatDate, formatName} from '@/utils/common_functions';
import { LoaderCircle } from 'lucide-react';

export const HospitalAdminScreen: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {showModal} = useConfimationModal();

  // ==========================
  // selectors
  // ==========================
  const hospitalAdminPocList = useSelector(hospitalAdminPocListSelector);
  const hospitalAdminPocListCurrentPage = useSelector(hospitalAdminPocListCurrentPageSelector);
  const hospitalAdminPocListTotalPages = useSelector(hospitalAdminPocListTotalPagesSelector);
  const hospitalAdminPocListTotalResults = useSelector(hospitalAdminPocListTotalResultsSelector);
  const hospitalAdminLoading = useSelector(hospitalAdminLoadingSelector);
  const hospitalAdminSuccess = useSelector(hospitalAdminSuccessSelector) as SuccessCodes;
  const hospitalAdminFailure = useSelector(hospitalAdminFailureSelector) as ErrorCodes;

  // ==========================
  // states
  // ==========================
  const [search, setSearch, immediateSearch] = useDebounceState<string>('');
  const [status, setStatus, immediateStatus] = useDebounceState<SelectInputitem | null>(null);
  const [from, setFrom, immediateFrom] = useDebounceState<Date | null>(null);
  const [to, setTo, immediateTo] = useDebounceState<Date | null>(null);
  const [page, setPage, immediatePage] = useDebounceState<number>(1);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [noData, setNoData] = useState(false);
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);

  // ==========================
  // function
  // ==========================
  const resetFilter = () => {
    setSearch('');
    setStatus(null);
    setFrom(null);
    setTo(null);
    setPage(1);
  };

  const fetchHospitalPocList = useCallback(() => {
    const payload: GetHospitalPocListRequestPayload = {
      page,
    };
    if (search){
      payload.search_text = search;
    }
    if (status) {
      payload.status = status.id === 1;
    }
    if (from) {
      payload.start_date = from.toISOString();
    }
    if (to) {
      payload.end_date = to.toISOString();
    }

    const isFilterActive = Boolean(search || status || from || to);
    setIsFilterActive(isFilterActive);
    setNoData(false);

    // fetch list
    dispatch(getHospitalPocListRequest(payload));
  }, [page, search, status, from, to]);

  const handleToggleStatus = (id: number, status: boolean) => {
    showModal({
      onConfirmHandler: () => dispatch(updateHospitalPocStatusRequest({id, status})),
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
      header: `Are you sure you want to ${status ? 'active' : 'block'} the user?`,
    });
  };


  const addHospitalPoc = (values: AddHospitalPocRequestPayload) => {
    dispatch(addHospitalPocRequest(values))
  }


  // ==========================
  // data
  // ==========================
  const columns = useMemo(
    (): DataTableColumn<HospitalPoc>[] => [
      {
        name: 'id',
        align: 'left',
        title: 'ADMIN ID',
        render: row => row.id,
        width: {minWidth: '120px', maxWidth: '150px'},
      },
      {
        name: 'name',
        align: 'left',
        title: 'ADMIN NAME',
        render: row => <Text className="break-words text-wrap">{formatName(row.name)}</Text>,
        width: {minWidth: '150px', maxWidth: '150px'},
      },
      {
        name: 'email',
        align: 'left',
        title: 'EMAIL ID',
        render: row => <Text className="break-words text-wrap">{row.email}</Text>,
        width: {minWidth: '200px', maxWidth: '350px'},
      },
      {
        name: 'phone',
        align: 'left',
        title: 'PHONE NO.',
        render: row => <Text className="break-words text-wrap">{row.phone}</Text>,
        width: {minWidth: '200px', maxWidth: '350px'},
      },
      {
        name: 'hospital',
        align: 'left',
        title: 'HOSPITAL NAME',
        render: row => <Text className="break-words text-wrap">{row.hospital_name}</Text>,
        width: {minWidth: '300px', maxWidth: '500px',},
      },
      {
        name: 'last_login',
        align: 'center',
        title: 'LAST LOGIN',
        render: row => <Text>{row.last_activity ? formatDate(row.last_activity, 'dd-MMM-yyyy') : NA}</Text>,
        width: {minWidth: '150px', maxWidth: '150px'},
      },
      {
        name: 'status',
        align: 'left',
        title: 'STATUS',
        render: row => (
          <div className="gap-4 flex items-center">
            <Status status={row.status} />
            <Switch value={row.status} onChange={value => handleToggleStatus(row.id, value)} />
          </div>
        ),
        width: {minWidth: '150px', maxWidth: '250px'},
      },
    ],
    [],
  );

  // ==========================
  // side effects
  // ==========================

  // use effect for fetching data
  useEffect(() => {
    fetchHospitalPocList();
  }, [fetchHospitalPocList]);

  // toast handler use effect
  useEffect(() => {
    if (hospitalAdminSuccess) {
      if (['S-10088', 'S-10089'].includes(hospitalAdminSuccess)) {
        showToast(getSuccessMessage(hospitalAdminSuccess), 'success');
      }

      if (hospitalAdminSuccess === 'S-10088'){
        // when hospital admin is added successfully, refetch the list
        fetchHospitalPocList();
        setIsAddAdminModalOpen(false);
      }
    }
    if (hospitalAdminFailure) {
      if (hospitalAdminFailure === 'E-10054') {
        setNoData(true);
      }
      if (!['E-10044', 'E-10054'].includes(hospitalAdminFailure)) {
        showToast(getErrorMessage(hospitalAdminFailure), 'failure');
      }
    }

    return () => {
      dispatch(resetHospitalAdminMessage());
    };
  }, [hospitalAdminSuccess, hospitalAdminFailure]);

  if (hospitalAdminLoading && !isFilterActive) {
    return (
      <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
        <span className="text-ui_grey">Loading...</span>
        <LoaderCircle className="size-4 animate-spin text-ui_grey ml-2" />
      </div>
    );
  }

  if (!hospitalAdminLoading && noData) {
    return (
      <div className="p-4 flex-col flex gap-2">
        <div className="bg-white shadow-md border border-ui_input_border py-8 sm:py-12 sm:px-4 px-2 flex flex-col gap-4 justify-center items-center rounded-3xl">
          <Text variant="heading" className="text-ui_blue text-center">
            No Hospital-Level Admins added yet!
          </Text>

          <Button leftIcon="plus_white" iconClassName="size-4" title="Add Admin" variant="primary" onClick={() => setIsAddAdminModalOpen(true)} />
        </div>

        {isAddAdminModalOpen && <AddHospitalPocModal onSave={addHospitalPoc} open={isAddAdminModalOpen} onClose={() => setIsAddAdminModalOpen(false)} />}
      </div>
    );
  }

  return (
    <div className="sm:p-4 md:p-6 lg:p-8 p-2 flex-col flex gap-4 md:gap-6 lg:gap-8">
      <div className="flex justify-between items-center gap-4">
        <Text variant="free" className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-ui_blue !font-FigtreeSemiBold md:!font-FigtreeBold">
          Hospital-Level Admin List
        </Text>
        <Button
          leftIcon="plus_white"
          textClassName="text-nowrap"
          iconClassName="size-4"
          title="Add Admin"
          variant="primary"
          onClick={() => setIsAddAdminModalOpen(true)}
        />
      </div>

      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={immediateSearch}
          onChangeText={setSearch}
          placeholder="Search by Admin Name, Email & Hospital"
          leftIcon="search_blue"
          wrapperClassName="pl-2 border min-w-[340px]"
          inputClassName="!pr-0"
          className="grow md:min-w-[240px] col-span-full md:max-w-[360px]"
        />
        <SelectInput
          value={immediateStatus?.id || ''}
          options={statusOptions}
          onChange={setStatus}
          placeholder="Status"
          wrapperClassName="border"
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
        />
        <DateInput
          value={immediateFrom}
          onChangeDate={setFrom}
          placeholder="From"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          textClassName="text-start text-ui_blue"
          maxValue={immediateTo ?? new Date()}
        />
        <DateInput
          value={immediateTo}
          onChangeDate={setTo}
          placeholder="To"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          textClassName="text-start text-ui_blue"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          minValue={immediateFrom ?? undefined}
          maxValue={new Date()}
        />

        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={hospitalAdminPocList}
        totalPages={hospitalAdminPocListTotalPages}
        currentPage={hospitalAdminPocListCurrentPage}
        totalResult={hospitalAdminPocListTotalResults}
        onPageChange={setPage}
        pageSize={5}
        loading={hospitalAdminLoading}
      />

      {isAddAdminModalOpen && <AddHospitalPocModal onSave={addHospitalPoc} open={isAddAdminModalOpen} onClose={() => setIsAddAdminModalOpen(false)} />}
    </div>
  );
};
